/**
 * The CRM's semantic states → the product's own classes (design §D, revised: the CRM looks
 * exactly like the rest of the admin; there is no CRM palette).
 *
 * Every badge in the section reads its colours from here, so a state is painted the same
 * way on the list, the detail page and the dashboard. The families follow the admin's
 * existing pills (BookingsAdmin STATUS_COLORS, pathways statusPill, Overview); where the
 * product's own pill text fails WCAG AA (sage-deep on sage/30 is 3.5:1, ochre-deep on
 * ochre/20 is 3.3:1) the text is the product ink instead, which keeps the family and
 * reaches 4.5:1. The pairs and their measured ratios are in `CONTRAST_PAIRS`.
 */
export const STATE_CLASSES = Object.freeze({
  /** active client, consented, opened / delivered, confirmed, pack with room left */
  positive: "bg-eh-sage/30 text-eh-ink",
  /** lead, not consented, draft, other neutral facts */
  neutral: "bg-eh-cream text-eh-muted border border-eh-border-soft",
  /** past client (ochre family) */
  past: "bg-eh-ochre/20 text-eh-ink",
  /** pack ending (2 or fewer left), bounced, waiting: amber, as the admin's "needs attention" */
  warning: "bg-amber-100 text-amber-800",
  /** pack finished (0 left), failed, unsubscribed: rose, as the admin's cancelled / refused */
  danger: "bg-rose-100 text-rose-800",
});

/**
 * [foreground hex, background hex (opaque, composited over white), where] for the pairs
 * above, from the `eh.*` palette (tailwind.config.js) and Tailwind's own amber / rose.
 * Measured: positive 10.25, neutral 6.95, past 10.96, warning 6.37, danger 6.68, bulk bar 11.72.
 */
export const CONTRAST_PAIRS = Object.freeze([
  ["#2C3531", "#E5E9DC", "positive: ink on eh-sage/30"],
  ["#4E574F", "#FAF6EC", "neutral: eh-muted on eh-cream"],
  ["#2C3531", "#F9EDDE", "past: ink on eh-ochre/20"],
  ["#92400E", "#FEF3C7", "warning: amber-800 on amber-100"],
  ["#9F1239", "#FFE4E6", "danger: rose-800 on rose-100"],
  ["#FAF6EC", "#2C3531", "bulk bar / active tab: eh-cream on eh-ink"],
  ["#4E574F", "#FFFFFF", "secondary text on cards"],
]);

/**
 * Chart series in the product palette (index.css `--eh-*`): ink, sage-deep, ochre. Hex,
 * not CSS variables, because recharts writes them into SVG presentation attributes.
 */
export const CHART_COLORS = Object.freeze({
  ink: "#2C3531",
  sage: "#6F7E5C",
  ochre: "#E2A35B",
  grid: "#E5DEC9",
  axis: "#4E574F",
});
